import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Typography, Card, Form, Input, Button, Divider, Space } from 'antd';
import {
    PhoneOutlined,
    LockOutlined,
    LoginOutlined,
    ArrowLeftOutlined,
    FileProtectOutlined,
    DollarOutlined,
    AlertOutlined,
    CustomerServiceOutlined,
    CheckCircleOutlined,
    SafetyCertificateOutlined,
} from '@ant-design/icons';
import { toast } from 'react-toastify';
import { ClientAuthContext } from '../../../common/constants';
import { authService } from '../../../common/services';
import './Connexion.scss';

const { Title, Paragraph, Text, Link } = Typography;

const avantages = [
    { icon: <FileProtectOutlined />, text: 'Consultez vos contrats d\'assurance a tout moment' },
    { icon: <DollarOutlined />, text: 'Suivez votre situation financiere et vos paiements' },
    { icon: <AlertOutlined />, text: 'Declarez un sinistre en ligne rapidement' },
    { icon: <CustomerServiceOutlined />, text: 'Contactez votre conseiller dedie' },
];

export default function Connexion() {
    const navigate = useNavigate();
    const auth = useContext(ClientAuthContext);
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [mode, setMode] = useState('login');

    const handleLogin = async (values) => {
        setLoading(true);
        try {
            const telephone = values.telephone.startsWith('+225')
                ? values.telephone
                : `+225${values.telephone.replace(/\s/g, '')}`;

            const response = await authService.login({
                telephone,
                password: values.password,
            });

            if (response.success) {
                toast.success('Connexion reussie !');
                auth.onLogin(response, navigate);
            } else {
                toast.error(response.message || 'Identifiants incorrects');
            }
        } catch (error) {
            toast.error(error.message || 'Erreur lors de la connexion');
        } finally {
            setLoading(false);
        }
    };

    const handleForgotPassword = async (values) => {
        setLoading(true);
        try {
            const telephone = values.telephone.startsWith('+225')
                ? values.telephone
                : `+225${values.telephone.replace(/\s/g, '')}`;

            await authService.forgotPassword({ telephone });
            toast.success('Si ce numero est enregistre, un code vous sera envoye par SMS.');
            setMode('login');
        } catch (error) {
            toast.error(error.message || 'Erreur lors de la demande');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="page-connexion">
            <div className="connexion-layout">
                {/* Panneau gauche - Avantages */}
                <div className="connexion-sidebar">
                    <div className="sidebar-content">
                        <SafetyCertificateOutlined className="sidebar-main-icon" />
                        <Title level={2} className="sidebar-title">Votre espace personnel</Title>
                        <Paragraph className="sidebar-desc">
                            Accedez a tous vos services d'assurance en un seul endroit.
                            Gerez vos contrats, suivez vos paiements et declarez vos sinistres facilement.
                        </Paragraph>

                        <div className="sidebar-avantages">
                            {avantages.map((a, i) => (
                                <div className="sidebar-avantage" key={i}>
                                    <div className="avantage-check"><CheckCircleOutlined /></div>
                                    <span>{a.text}</span>
                                </div>
                            ))}
                        </div>

                        <div className="sidebar-help">
                            <Text className="help-text">Besoin d'aide pour vous connecter ?</Text>
                            <Text className="help-phone">+225 07 00 00 00 00</Text>
                        </div>
                    </div>
                </div>

                {/* Panneau droit - Formulaire */}
                <div className="connexion-form-area">
                    <div className="form-container">
                        {mode === 'login' ? (
                            <>
                                <Title level={2} className="form-title">Connexion</Title>
                                <Paragraph className="form-subtitle">
                                    Entrez vos identifiants pour acceder a votre espace client
                                </Paragraph>

                                <Form
                                    form={form}
                                    layout="vertical"
                                    onFinish={handleLogin}
                                    size="large"
                                    className="login-form"
                                >
                                    <Form.Item
                                        name="telephone"
                                        label="Numero de telephone"
                                        rules={[{ required: true, message: 'Veuillez saisir votre numero' }]}
                                    >
                                        <Input
                                            prefix={<PhoneOutlined style={{ color: '#999' }} />}
                                            placeholder="07 XX XX XX XX"
                                            addonBefore="+225"
                                        />
                                    </Form.Item>

                                    <Form.Item
                                        name="password"
                                        label="Mot de passe"
                                        rules={[{ required: true, message: 'Veuillez saisir votre mot de passe' }]}
                                    >
                                        <Input.Password
                                            prefix={<LockOutlined style={{ color: '#999' }} />}
                                            placeholder="Votre mot de passe"
                                        />
                                    </Form.Item>

                                    <div style={{ textAlign: 'right', marginBottom: 24 }}>
                                        <Link onClick={() => setMode('forgot')}>
                                            Mot de passe oublie ?
                                        </Link>
                                    </div>

                                    <Button
                                        type="primary"
                                        htmlType="submit"
                                        loading={loading}
                                        icon={<LoginOutlined />}
                                        block
                                        className="login-btn"
                                    >
                                        Se connecter
                                    </Button>
                                </Form>

                                <Divider className="form-divider">
                                    <Text type="secondary" style={{ fontSize: 13 }}>Pas encore de compte ?</Text>
                                </Divider>

                                <div style={{ textAlign: 'center' }}>
                                    <Paragraph type="secondary" style={{ marginBottom: 12 }}>
                                        Pour creer votre acces a l'espace client, contactez votre agence CAP Assurances.
                                    </Paragraph>
                                    <Button onClick={() => navigate('/contact')} size="large" block>
                                        Contacter une agence
                                    </Button>
                                </div>
                            </>
                        ) : (
                            <>
                                <Button
                                    type="text"
                                    icon={<ArrowLeftOutlined />}
                                    onClick={() => setMode('login')}
                                    style={{ marginBottom: 16, padding: 0 }}
                                >
                                    Retour a la connexion
                                </Button>

                                <Title level={2} className="form-title">Mot de passe oublie</Title>
                                <Paragraph className="form-subtitle">
                                    Saisissez votre numero de telephone. Si un compte existe,
                                    vous recevrez un code de reinitialisation par SMS.
                                </Paragraph>

                                <Form
                                    layout="vertical"
                                    onFinish={handleForgotPassword}
                                    size="large"
                                    className="login-form"
                                >
                                    <Form.Item
                                        name="telephone"
                                        label="Numero de telephone"
                                        rules={[{ required: true, message: 'Veuillez saisir votre numero' }]}
                                    >
                                        <Input
                                            prefix={<PhoneOutlined style={{ color: '#999' }} />}
                                            placeholder="07 XX XX XX XX"
                                            addonBefore="+225"
                                        />
                                    </Form.Item>

                                    <Button
                                        type="primary"
                                        htmlType="submit"
                                        loading={loading}
                                        block
                                        className="login-btn"
                                    >
                                        Envoyer le code
                                    </Button>
                                </Form>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
